import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '../../components/UI/Button';
import LoadingOverlay from '../../components/UI/LoadingOverlay';
import { useAuth } from '../../context/AuthContext';
import { userApi } from '../../services/api';
import { LegalDocument } from '../../types';

const schema = z.object({
  termsAccepted: z.boolean().refine((v) => v === true, {
    message: 'You must accept the Terms of Service',
  }),
  privacyAccepted: z.boolean().refine((v) => v === true, {
    message: 'You must accept the Privacy Policy',
  }),
});

type FormData = z.infer<typeof schema>;

const AcceptLegalPage: React.FC = () => {
  const { user, acceptLegalDocuments, logout } = useAuth();
  const [terms, setTerms] = useState<LegalDocument | null>(null);
  const [privacy, setPrivacy] = useState<LegalDocument | null>(null);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { termsAccepted: false, privacyAccepted: false },
  });

  useEffect(() => {
    let cancelled = false;
    userApi.getCurrentLegalDocuments()
      .then((r) => {
        if (cancelled) return;
        setTerms(r.data.terms ?? null);
        setPrivacy(r.data.privacy ?? null);
      })
      .catch(() => {
        if (!cancelled) setError('Unable to load the latest Terms and Privacy Policy.');
      })
      .finally(() => {
        if (!cancelled) setLoadingDocs(false);
      });
    return () => { cancelled = true; };
  }, []);

  const onSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await acceptLegalDocuments({
        termsAccepted: true,
        privacyAccepted: true,
        termsVersion: terms?.version,
        privacyVersion: privacy?.version,
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not save your acceptance.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto px-4 py-12">
      <div className="surface-card p-6 sm:p-8">
      {(loadingDocs || submitting) && <LoadingOverlay />}
      <h1 className="text-2xl font-bold text-gray-900">Review and accept</h1>
      <p className="mt-2 text-sm text-gray-600">
        Before continuing{user?.email ? ` as ${user.email}` : ''}, please accept the current Terms of Service
        and Privacy Policy.
      </p>
      {(terms?.version || privacy?.version) && (
        <p className="mt-2 text-xs text-gray-500">
          {terms?.version ? `Terms v${terms.version}` : null}
          {terms?.version && privacy?.version ? ' · ' : null}
          {privacy?.version ? `Privacy v${privacy.version}` : null}
        </p>
      )}

      {error && (
        <div className="mt-4 p-3 bg-error-50 text-error-600 text-sm rounded-md border border-error-200">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
        <label className="flex gap-3 text-sm text-gray-700">
          <input type="checkbox" className="mt-1" {...register('termsAccepted')} />
          <span>
            I agree to the{' '}
            <Link to="/terms" target="_blank" className="text-primary-600 hover:underline">
              Terms of Service
            </Link>
            {terms?.version ? ` (v${terms.version})` : ''}.
          </span>
        </label>
        {errors.termsAccepted && <p className="text-sm text-error-600">{errors.termsAccepted.message}</p>}

        <label className="flex gap-3 text-sm text-gray-700">
          <input type="checkbox" className="mt-1" {...register('privacyAccepted')} />
          <span>
            I agree to the{' '}
            <Link to="/privacy" target="_blank" className="text-primary-600 hover:underline">
              Privacy Policy
            </Link>
            {privacy?.version ? ` (v${privacy.version})` : ''}.
          </span>
        </label>
        {errors.privacyAccepted && <p className="text-sm text-error-600">{errors.privacyAccepted.message}</p>}

        <div className="flex flex-wrap gap-3 pt-2">
          <Button type="submit" disabled={loadingDocs || !terms || !privacy}>
            Accept and continue
          </Button>
          <Button type="button" variant="outline" onClick={() => logout()}>
            Sign out
          </Button>
        </div>
      </form>
      </div>
    </div>
  );
};

export default AcceptLegalPage;
